import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ApiError, type AuthUser } from './api.js';
import { setLiveSessionChecker, type ResultadoSessaoLive } from './useLiveEvents.js';

/**
 * Resultado do primeiro passo do login: ou já autenticou (2FA off) ou o
 * caller precisa completar via `verifyLoginTwoFactor(code)` com o token
 * devolvido aqui.
 */
export type LoginStep =
  | { kind: 'authenticated' }
  | { kind: 'requires_2fa'; challengeToken: string };

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  /** Primeiro passo do login: senha. Devolve o estado seguinte. */
  login: (email: string, password: string) => Promise<LoginStep>;
  /** Segundo passo do login quando o usuário tem 2FA ativado. */
  verifyLoginTwoFactor: (challengeToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .me()
      .then(({ user }) => setUser(user))
      .catch((e) => {
        if (!(e instanceof ApiError) || e.status !== 401) console.error(e);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string): Promise<LoginStep> => {
    const res = await api.login(email, password);
    if ('requires2fa' in res && res.requires2fa) {
      return { kind: 'requires_2fa', challengeToken: res.challengeToken };
    }
    setUser(res.user);
    return { kind: 'authenticated' };
  };

  const verifyLoginTwoFactor = async (challengeToken: string, code: string) => {
    const { user } = await api.verifyLoginTwoFactor(challengeToken, code);
    setUser(user);
  };

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
    }
  };

  const refresh = async () => {
    try {
      const { user } = await api.me();
      setUser(user);
    } catch {
      setUser(null);
    }
  };

  // Ponte entre o canal `/ws/live` e o estado de sessão.
  //
  // O servidor fecha o socket com 1008 em quatro situações e só uma delas
  // mata a sessão (ver `CODIGO_SESSAO_INVALIDA`): nas outras três — role,
  // garageScope, globalGarageAccess — derrubar o socket é como o servidor
  // faz o cliente reabrir com as concessões novas. O cliente do WS é um
  // singleton de módulo, fora da árvore React, e não tem como perguntar
  // `/api/auth/me` por conta própria; quem sabe fazer isso é este provider.
  //
  // **A ponte devolve o resultado** em vez de avisar por um segundo canal.
  // Uma ida e volta é uma coisa só para revisar: não existe um
  // `resolveSessionCheck()` público que alguém possa chamar fora de hora,
  // nem estado de "verificação pendente" duplicado dos dois lados.
  //
  // Não reusa `refresh()` porque ele responde a outra pergunta: em
  // QUALQUER erro ele zera o usuário, e aqui uma queda de rede não pode
  // significar "você foi desconectado". Só o 401 derruba.
  //
  // `setUser` é estável (setter de `useState`), então o efeito roda uma vez
  // e não precisa de `ref` para alcançar a versão corrente.
  useEffect(() => {
    setLiveSessionChecker(async (): Promise<ResultadoSessaoLive> => {
      try {
        const { user: atual } = await api.me();
        // 200: a sessão vale. Isto aqui é o ponto do exercício — as
        // concessões novas entram na UI, que era o que o servidor queria
        // ao derrubar o socket.
        setUser(atual);
        return 'valida';
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          setUser(null);
          return 'invalida';
        }
        // Rede, 500, DNS: não dá para afirmar nada sobre a sessão, e
        // derrubar o usuário por indisponibilidade momentânea seria
        // inventar um fato.
        return 'indeterminada';
      }
    });
    return () => setLiveSessionChecker(null);
  }, []);

  return (
    <AuthCtx.Provider
      value={{ user, loading, login, verifyLoginTwoFactor, logout, refresh }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const v = useContext(AuthCtx);
  if (!v) throw new Error('useAuth precisa de <AuthProvider>');
  return v;
}
